import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/adminAPI';
import Navbar from '../components/Navbar';
import './AdminSeasons.css';

function AdminSeasons() {
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    isActive: true,
  });

  useEffect(() => {
    fetchSeasons();
  }, []);

  const fetchSeasons = async () => {
    try {
      const response = await adminAPI.getAllSeasons();
      setSeasons(response.data);
    } catch (error) {
      console.error('Error fetching seasons:', error);
      alert('Failed to load seasons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();

    try {
      await adminAPI.createSeason(formData);
      alert('Season created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchSeasons();
    } catch (error) {
      console.error('Create season error:', error);
      alert(error.response?.data?.error || 'Failed to create season');
    }
  };

  const handleUpdateSeason = async (e) => {
    e.preventDefault();

    try {
      await adminAPI.updateSeason(editingSeason.id, formData);
      alert('Season updated successfully!');
      setEditingSeason(null);
      resetForm();
      fetchSeasons();
    } catch (error) {
      console.error('Update season error:', error);
      alert('Failed to update season');
    }
  };

  const handleDeleteSeason = async (season) => {
    if (
      !window.confirm(
        `Delete season "${season.name}"? Teams using this season will keep their data.`
      )
    ) {
      return;
    }

    try {
      await adminAPI.deleteSeason(season.id);
      alert('Season deleted successfully');
      fetchSeasons();
    } catch (error) {
      console.error('Delete season error:', error);
      alert('Failed to delete season');
    }
  };

  const handleGenerateSeasons = async () => {
    const years = prompt(
      'How many years ahead should we generate seasons?',
      '2'
    );
    if (!years) return;

    try {
      const response = await adminAPI.generateSeasons(parseInt(years));
      alert(response.data.message);
      fetchSeasons();
    } catch (error) {
      console.error('Generate seasons error:', error);
      alert('Failed to generate seasons');
    }
  };

  const handleEdit = (season) => {
    setEditingSeason(season);
    setFormData({
      name: season.name,
      startDate: season.startDate.split('T')[0],
      endDate: season.endDate.split('T')[0],
      isActive: season.isActive,
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      startDate: '',
      endDate: '',
      isActive: true,
    });
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingSeason(null);
    resetForm();
  };

  const isPast = (endDate) => {
    return new Date(endDate) < new Date();
  };

  const isCurrent = (startDate, endDate) => {
    const now = new Date();
    return new Date(startDate) <= now && new Date(endDate) >= now;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="admin-container">
          <div className="loading-state">Loading seasons...</div>
        </div>
      </>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <Navbar />
      <div className="admin-container">
        <div className="admin-content">
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Back to Admin Home
          </button>

          <div className="seasons-admin-header">
            <div>
              <h1>Season Management</h1>
              <p>Manage seasons for team creation</p>
            </div>
            <div className="header-actions">
              <button className="btn-generate" onClick={handleGenerateSeasons}>
                🔄 Auto-Generate Seasons
              </button>
              <button
                className="btn-create-season"
                onClick={() => setShowCreateModal(true)}
              >
                + Create Season
              </button>
            </div>
          </div>

          <div className="seasons-table-container">
            <table className="seasons-table">
              <thead>
                <tr>
                  <th>Season Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {seasons.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-message">
                      No seasons yet. Create one or auto-generate!
                    </td>
                  </tr>
                ) : (
                  seasons.map((season) => (
                    <tr key={season.id}>
                      <td>
                        <strong>{season.name}</strong>
                      </td>
                      <td>{new Date(season.startDate).toLocaleDateString()}</td>
                      <td>{new Date(season.endDate).toLocaleDateString()}</td>
                      <td>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            flexWrap: 'wrap',
                          }}
                        >
                          {isCurrent(season.startDate, season.endDate) && (
                            <span className="badge-current">Current</span>
                          )}
                          {isPast(season.endDate) && (
                            <span className="badge-past">Past</span>
                          )}
                          {!season.isActive && (
                            <span className="badge-inactive">Inactive</span>
                          )}
                          {!isCurrent(season.startDate, season.endDate) &&
                            !isPast(season.endDate) &&
                            season.isActive && (
                              <span className="badge-future">Future</span>
                            )}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-edit-small"
                            onClick={() => handleEdit(season)}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            className="btn-delete-small"
                            onClick={() => handleDeleteSeason(season)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {seasons.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🗓️</div>
              <h3>No Seasons Yet</h3>
              <p>Create your first season or auto-generate them!</p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingSeason) && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSeason ? 'Edit Season' : 'Create New Season'}</h2>
              <button className="modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>

            <form
              onSubmit={editingSeason ? handleUpdateSeason : handleCreateSeason}
            >
              <div className="modal-body">
                <div className="form-group">
                  <label>Season Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g., Winter 2025-2026"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date *</label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) =>
                        setFormData({ ...formData, startDate: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>End Date *</label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) =>
                        setFormData({ ...formData, endDate: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                    />{' '}
                    Active (show in team creation dropdown)
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSeason ? '💾 Update Season' : '✓ Create Season'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSeasons;
